// src/pages/Contacts.jsx
import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, Filter, Mail, Clock, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { adminContactsAPI } from '../api';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await adminContactsAPI.list();
      setContacts(response.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await adminContactsAPI.updateStatus(contactId, newStatus);
      // Update local state
      setContacts(contacts.map(contact => 
        contact.id === contactId 
          ? { ...contact, status: newStatus }
          : contact
      ));
    } catch (error) {
      console.error('Failed to update contact status:', error);
      alert('Failed to update contact status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock className="w-4 h-4" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4" />;
      case 'responded': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'text-blue-400 bg-blue-900/30';
      case 'in_progress': return 'text-yellow-400 bg-yellow-900/30';
      case 'responded': return 'text-green-400 bg-green-900/30';
      case 'closed': return 'text-gray-400 bg-gray-900/30';
      default: return 'text-blue-400 bg-blue-900/30';
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Contact Management</h2>
          <p className="text-slate-400 mt-2">View and manage contact form submissions</p>
        </div>
        <button className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center">
          <Mail className="w-4 h-4 mr-2" />
          Send Email
        </button>
      </div>

      {/* Search and Filters */}
      <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors flex items-center">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-100 mb-4">
            Contact Submissions ({filteredContacts.length})
          </h3>
          
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-slate-400">Loading contacts...</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-400 text-lg">Error loading contacts</p>
                <p className="text-slate-500">{error}</p>
                <button 
                  onClick={fetchContacts}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="h-96 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No contact submissions found</p>
                <p className="text-slate-500">Contact submissions will appear here when received</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Contact</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Subject</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact) => (
                    <tr key={contact.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-slate-100">{contact.name}</div>
                          <div className="text-sm text-slate-400">{contact.email}</div>
                          {contact.company && (
                            <div className="text-xs text-slate-500">{contact.company}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-slate-100">{contact.subject}</div>
                        <div className="text-sm text-slate-400 truncate max-w-xs">
                          {contact.message}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                          {getStatusIcon(contact.status)}
                          <span className="ml-1 capitalize">{contact.status.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-sm">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <select
                          value={contact.status}
                          onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In Progress</option>
                          <option value="responded">Responded</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contacts;
